import {AxiosInstance, AxiosResponse} from "axios";
import {
    Dosage,
    DosageCreateRequest,
    DosageFrequency,
    DosageInstruction,
    DosagePrefillItem,
    DosageTimeTrigger
} from "@/dosage/interfaces/Dosage";
import {ProductDose} from "@/product/interfaces/ProductDose";

const DosageResource = (hdsClient: AxiosInstance, apiClient: AxiosInstance) => {
    return {
        /**
         * Get the dosage time triggers.
         */
        async indexTimeTrigger(): Promise<DosageTimeTrigger[]> {
            return hdsClient.get(`/dosages/time-triggers`)
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Get the product doses.
         */
        async productDoses(params: { limit?: number; query?: string } = {limit: 10}): Promise<ProductDose[]> {
            return apiClient.get('products/doses', {
                params
            })
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Get the advised dose for a product.
         *
         * @param productId
         */
        async productDose(productId: number): Promise<ProductDose | null> {
            return apiClient.get(`products/${productId}/dose`)
                .then((response: AxiosResponse) => response.data.data)
                .catch(() => null);
        },

        /**
         * Get the dosage instructions.
         */
        async dosageInstructions(): Promise<DosageInstruction[]> {
            return hdsClient.get(`/dosages/instructions`)
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Get the dosage frequencies.
         */
        async dosageFrequencies(): Promise<DosageFrequency[]> {
            return hdsClient.get("dosages/frequencies")
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Get the dosage template for a product.
         */
        async dosageTemplates(productId: number): Promise<Record<string, Dosage[]>> {
            return hdsClient.get('dosage-templates', {
                params: {product_id: productId}
            })
                .then((response: AxiosResponse) => response.data);
        },

        /**
         * Create a new dosage.
         */
        async createDosage(data: DosageCreateRequest): Promise<Dosage> {
            return hdsClient.post(`dosages`, {data})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Update an existing dosage.
         */
        async updateDosage(id: number, data: DosageCreateRequest): Promise<Dosage> {
            return hdsClient.patch(`dosages/${id}`, {data})
                .then((response: AxiosResponse) => response.data.data);
        },

        /**
         * Ask the API to extract dosages from a prescription image for the
         * given products. Returns one entry per requested product_id (the
         * `dosage` is null if the agent couldn't find it).
         */
        async prefill(image: Blob, productIds: number[]): Promise<DosagePrefillItem[]> {
            const formData = new FormData();
            formData.append('image', image);
            productIds.forEach(id => formData.append('product_ids[]', String(id)));

            return apiClient.post('/dosage/prefill', formData)
                .then((response: AxiosResponse) => response.data.data);
        }
    };
};

export default DosageResource;
